import api from '../utils/apiInterceptor';

/**
 * 任务服务 - 提供与后端任务API交互的函数
 */

/**
 * 创建章节生成任务
 * @param {Object} taskData 任务数据
 * @returns {Promise<Object>} 任务创建结果
 */
export const createChapterGenerationTask = async (taskData) => {
  try {
    const response = await api.post('/tasks/generate-chapter', taskData);
    
    // 检查任务状态
    if (response.data && response.data.data && response.data.data.taskId) {
      // 立即检查任务状态，确保任务已开始处理
      await checkTaskStarted(response.data.data.taskId);
    }
    
    return response.data;
  } catch (error) {
    console.error('创建章节生成任务失败:', error);
    throw error;
  }
};

/**
 * 检查任务是否已开始处理
 * @param {string} taskId 任务ID
 * @returns {Promise<boolean>} 是否已开始处理
 */
const checkTaskStarted = async (taskId) => {
  try {
    // 尝试最多3次，每次间隔1秒
    for (let i = 0; i < 3; i++) {
      // 等待1秒
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 获取任务状态
      const response = await api.get(`/tasks/status/${taskId}`);
      
      if (response.data && response.data.data) {
        const status = response.data.data.status;
        
        // 如果任务状态为处理中或已完成，则表示已开始处理
        if (status === 'processing' || status === 'completed') {
          console.log(`任务 ${taskId} 已开始处理，状态: ${status}`);
          return true;
        }
        
        console.log(`任务 ${taskId} 当前状态: ${status}，继续等待...`);
      }
    }
    
    console.warn(`任务 ${taskId} 可能未开始处理，请检查队列服务`);
    return false;
  } catch (error) {
    console.error(`检查任务 ${taskId} 状态失败:`, error);
    return false;
  }
};

/**
 * 创建批量生成任务
 * @param {Object} taskData 任务数据
 * @returns {Promise<Object>} 任务创建结果
 */
export const createBatchGenerationTask = async (taskData) => {
  try {
    // 记录请求数据
    console.log('批量生成任务请求数据:', {
      novelId: taskData.novelId,
      chapterIdsType: typeof taskData.chapterIds,
      isArray: Array.isArray(taskData.chapterIds),
      chapterIds: JSON.stringify(taskData.chapterIds).substring(0, 100),
      userId: taskData.userId || '未指定'
    });
    
    // 确保chapterIds是数组且不为空
    if (!taskData.chapterIds || !Array.isArray(taskData.chapterIds) || taskData.chapterIds.length === 0) {
      throw new Error('没有有效的章节ID');
    }
    
    // 修复可能的截断字符串问题
    if (typeof taskData.chapterIds === 'string') {
      try {
        // 尝试解析JSON字符串
        taskData.chapterIds = JSON.parse(taskData.chapterIds);
      } catch (e) {
        console.error('解析章节ID字符串失败:', e);
        throw new Error('章节ID格式不正确');
      }
    }
    
    // 确保所有章节ID都是有效的字符串
    taskData.chapterIds = taskData.chapterIds.map(id => {
      // 确保ID是字符串类型
      if (id === null || id === undefined) return null;
      return id.toString(); // 将所有ID转换为字符串
    }).filter(id => id !== null && id !== undefined);
    
    if (taskData.chapterIds.length === 0) {
      throw new Error('没有有效的章节ID');
    }
    
    // 发送任务请求到正确的端点
    const response = await api.post('/tasks/generate-batch', taskData);
    
    // 记录响应信息
    console.log('批量生成请求响应:', JSON.stringify(response.data));
    
    // 记录更多详细信息（taskId 在 response.data.data 层级，与 NovelEditor 的 result.data.taskId 对齐）
    const createdTask = response.data.data || {};
    console.log('批量生成任务创建结果:', {
      taskId: createdTask.taskId,
      jobId: createdTask.jobId,
      status: createdTask.status
    });

    // 进行响应检查
    if (!response.data || !response.data.success) {
      console.error('服务器返回错误响应:', response.data);
      throw new Error(response.data?.message || '服务器返回错误响应');
    }

    // 检查必要字段
    if (!createdTask.taskId) {
      console.warn('服务器未返回taskId，但任务可能已创建');
    }
    
    return response.data;
  } catch (error) {
    // 详细记录错误信息
    console.error('创建批量生成任务失败:', error);
    
    // 处理不同类型的错误
    if (error.response) {
      // 服务器返回了错误响应
      console.error('服务器错误:', error.response.status, error.response.data);
      throw new Error(error.response.data?.message || `服务器错误 (${error.response.status})`);
    } else if (error.request) {
      // 请求已发送但没有收到响应
      console.error('未收到服务器响应:', error.request);
      throw new Error('服务器处理超时，请稍后重试');
    } else {
      // 请求设置时出错
      console.error('请求错误:', error.message);
    }
    
    throw error;
  }
};

/**
 * 创建章节优化任务
 * @param {Object} taskData 任务数据
 * @returns {Promise<Object>} 任务创建结果
 */
export const createOptimizeChapterTask = async (taskData) => {
  try {
    const response = await api.post('/tasks/optimize-chapter', taskData);
    return response.data;
  } catch (error) {
    console.error('创建章节优化任务失败:', error);
    throw error;
  }
};

/**
 * 获取任务状态
 * @param {string} taskId 任务ID
 * @returns {Promise<Object>} 任务状态
 */
export const getTaskStatus = async (taskId) => {
  try {
    const response = await api.get(`/tasks/status/${taskId}`);
    
    // 提取详细状态信息
    if (response.data && response.data.data) {
      const task = response.data.data;
      
      // 添加详细状态信息
      let detailedStatus = '等待中';
      if (task.status === 'processing') {
        // 从params中获取详细状态
        if (task.params && task.params.detailedStatus) {
          detailedStatus = task.params.detailedStatus;
        } else if (task.params && task.params.currentStep) {
          detailedStatus = task.params.currentStep;
        } else {
          detailedStatus = '生成中';
        }
      } else if (task.status === 'completed') {
        detailedStatus = '已完成';
      } else if (task.status === 'failed') {
        detailedStatus = task.result?.message || '生成失败';
      }
      
      // 添加到返回数据中
      response.data.detailedStatus = detailedStatus;
    }
    
    return response.data;
  } catch (error) {
    console.error('获取任务状态失败:', error);
    throw error;
  }
};

/**
 * 取消任务
 * @param {string} taskId 任务ID
 * @returns {Promise<Object>} 取消结果
 */
export const cancelTask = async (taskId) => {
  try {
    const response = await api.delete(`/tasks/${taskId}`);
    return response.data;
  } catch (error) {
    console.error('取消任务失败:', error);
    throw error;
  }
};

/**
 * 轮询任务状态直到完成
 * @param {string} taskId 任务ID
 * @param {Object} options 轮询选项
 * @returns {Promise<Object>} 最终的任务状态
 */
export const pollTaskStatus = async (taskId, options = {}) => {
  const {
    interval = 2000,             // 轮询间隔（毫秒）
    timeout = 30 * 60 * 1000,    // 超时时间（默认30分钟）
    onProgress = null,           // 进度回调
    onComplete = null,           // 完成回调
    onError = null               // 错误回调
  } = options;
  
  let elapsed = 0;
  
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const result = await getTaskStatus(taskId);
        const task = result.data;
        
        // 提取详细状态信息
        let detailedStatus = '等待中';
        if (task.status === 'processing') {
          // 从params中获取详细状态
          if (task.params && task.params.detailedStatus) {
            detailedStatus = task.params.detailedStatus;
          } else if (task.params && task.params.currentStep) {
            detailedStatus = task.params.currentStep;
          } else {
            detailedStatus = '生成中';
          }
        } else if (task.status === 'completed') {
          detailedStatus = '已完成';
        } else if (task.status === 'failed') {
          detailedStatus = task.result?.message || '生成失败';
        }
        
        // 添加详细状态到任务对象
        task.detailedStatus = detailedStatus;
        
        // 调用进度回调
        if (onProgress && typeof onProgress === 'function') {
          onProgress(task);
        }
        
        // 检查任务是否已完成
        if (task.status === 'completed') {
          // 调用完成回调
          if (onComplete && typeof onComplete === 'function') {
            onComplete(task);
          }
          return resolve(task);
        }
        
        // 检查任务是否失败
        if (task.status === 'failed') {
          const error = new Error(task.result?.message || '任务失败');
          error.task = task;
          
          // 调用错误回调
          if (onError && typeof onError === 'function') {
            onError(error);
          }
          return reject(error);
        }
        
        // 检查是否超时
        elapsed += interval;
        if (elapsed >= timeout) {
          const error = new Error('任务轮询超时');
          error.task = task;
          
          // 调用错误回调
          if (onError && typeof onError === 'function') {
            onError(error);
          }
          return reject(error);
        }
        
        // 继续轮询
        setTimeout(poll, interval);
      } catch (error) {
        // 调用错误回调
        if (onError && typeof onError === 'function') {
          onError(error);
        }
        reject(error);
      }
    };
    
    // 开始轮询
    poll();
  });
};

/**
 * 创建章节生成任务 (新接口，适配BackgroundGenerateButton组件)
 * @param {Object} taskData 任务数据
 * @returns {Promise<Object>} 任务创建结果
 */
export const createGenerationTask = async (taskData) => {
  try {
    const response = await api.post('/tasks/generate-chapter', taskData);
    return response.data;
  } catch (error) {
    console.error('创建章节生成任务失败:', error);
    throw error;
  }
};

/**
 * 创建章节优化任务 (新接口，适配BackgroundGenerateButton组件)
 * @param {Object} taskData 任务数据
 * @returns {Promise<Object>} 任务创建结果
 */
export const createOptimizeTask = async (taskData) => {
  try {
    console.log('【优化任务服务】接收到原始数据:', JSON.stringify({
      novelId: taskData.novelId,
      novelIdType: typeof taskData.novelId,
      chapterId: taskData.chapterId,
      chapterIdType: typeof taskData.chapterId,
      hasContent: !!taskData.content,
      contentType: typeof taskData.content,
      contentLength: taskData.content ? taskData.content.length : 0,
      requestId: taskData.requestId,
      timestamp: taskData.timestamp
    }));
    
    // 提取内容参数，统一处理字符串内容
    const content = taskData.content || '';
    
    // 简化ID转换，确保始终为字符串类型
    const novelId = taskData.novelId ? taskData.novelId.toString() : null;
    const chapterId = taskData.chapterId ? taskData.chapterId.toString() : null;
    const requestId = taskData.requestId || `opt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    console.log(`【优化任务服务】参数处理后 [requestId: ${requestId}]:`, {
      novelId,
      novelIdType: typeof novelId,
      chapterId,
      chapterIdType: typeof chapterId,
      contentLength: content.length,
      hasRequestId: !!requestId
    });
    
    // 验证必要参数
    if (!novelId) {
      console.error(`【优化任务服务】缺少必要参数: novelId [requestId: ${requestId}]`);
      throw new Error('缺少必要参数: novelId');
    }
    if (!chapterId) {
      console.error(`【优化任务服务】缺少必要参数: chapterId [requestId: ${requestId}]`);
      throw new Error('缺少必要参数: chapterId');
    }
    if (!content || content.trim().length < 100) {
      console.error(`【优化任务服务】内容验证失败 [requestId: ${requestId}]:`, {
        hasContent: !!content,
        contentLength: content ? content.trim().length : 0
      });
      throw new Error('章节内容过短，无法进行优化 (最少需要100个字符)');
    }
    
    // 准备请求数据
    const requestData = {
      novelId,
      chapterId,
      content,
      requestId,
      timestamp: taskData.timestamp || Date.now()
    };
    
    console.log(`【优化任务服务】准备发送请求数据 [requestId: ${requestId}]:`, {
      novelId: requestData.novelId,
      chapterId: requestData.chapterId,
      contentLength: requestData.content.length,
      timestamp: requestData.timestamp
    });
    
    // 发送请求
    const response = await api.post('/tasks/optimize-chapter', requestData);
    
    console.log(`【优化任务服务】请求成功 [requestId: ${requestId}]:`, {
      status: response.status,
      hasData: !!response.data,
      taskId: response.data?.data?.taskId,
      responseRequestId: response.data?.requestId
    });
    
    // 确保响应中包含请求ID
    if (response.data && !response.data.requestId) {
      response.data.requestId = requestId;
    }
    
    return response.data;
  } catch (error) {
    console.error('【优化任务服务】创建失败:', error.message, error.stack);
    throw error;
  }
};

/**
 * 🔥 创建章节重写任务
 * @param {Object} taskData 任务数据
 * @returns {Promise<Object>} 任务创建结果
 */
export const createRewriteTask = async (taskData) => {
  try {
    console.log('【重写任务服务】接收到原始数据:', JSON.stringify({
      novelId: taskData.novelId,
      chapterId: taskData.chapterId,
      hasContent: !!taskData.content,
      contentLength: taskData.content ? taskData.content.length : 0,
      requestId: taskData.requestId,
      timestamp: taskData.timestamp
    }));
    
    const content = taskData.content || '';
    const novelId = taskData.novelId ? taskData.novelId.toString() : null;
    const chapterId = taskData.chapterId ? taskData.chapterId.toString() : null;
    const requestId = taskData.requestId || `rewrite-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    console.log(`【重写任务服务】参数处理后 [requestId: ${requestId}]:`, {
      novelId,
      chapterId,
      contentLength: content.length
    });
    
    // 验证必要参数
    if (!novelId) {
      throw new Error('缺少必要参数: novelId');
    }
    if (!chapterId) {
      throw new Error('缺少必要参数: chapterId');
    }
    if (!content || content.trim().length < 100) {
      throw new Error('章节内容过短，无法进行重写 (最少需要100个字符)');
    }
    
    const requestData = {
      novelId,
      chapterId,
      content,
      requestId,
      timestamp: taskData.timestamp || Date.now()
    };
    
    console.log(`【重写任务服务】准备发送请求数据 [requestId: ${requestId}]`);
    
    // 发送请求
    const response = await api.post('/tasks/rewrite-chapter', requestData);
    
    console.log(`【重写任务服务】请求成功 [requestId: ${requestId}]:`, {
      status: response.status,
      taskId: response.data?.data?.taskId
    });
    
    if (response.data && !response.data.requestId) {
      response.data.requestId = requestId;
    }
    
    return response.data;
  } catch (error) {
    console.error('【重写任务服务】创建失败:', error.message);
    throw error;
  }
};

const taskService = {
  createChapterGenerationTask,
  createBatchGenerationTask,
  createOptimizeChapterTask,
  getTaskStatus,
  cancelTask,
  pollTaskStatus,
  createGenerationTask,
  createOptimizeTask,
  createRewriteTask
};

export default taskService;